import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ActivityBadge from "@/app/domain/dashboard/components/ActivityBadge";
import Avatar from "@/design/components/Avatar";
import Timer from "@/design/components/Timer";
import FormGroup from "@/design/forms/FormGroup";
import { showSnackbar } from "@/store/reducers/app";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import { toggleLoadingState } from "@/utils/utility";
import { viLocationPinDot } from "@sippy-platform/valkyrie";

import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { collection, getFirestore, onSnapshot, query } from "@firebase/firestore";
import { Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import PropTypes from "prop-types";

function QueueCard(props) {
  const dispatch = useDispatch();
  const colors = useAccessibleColors();

  const pbx = useSelector((state) => state.domain.pbx);
  const queueMembersStore = useSelector((state) => state.domain.queueMembers);

  const [calls, setCalls] = useState([]);

  useEffect(() => {
    let unsubscribeActivity;

    const auth = getAuth();
    let unsubscribeUser = onAuthStateChanged(auth, (user) => {
      if (user && props.queue.exten) {
        let db = getFirestore();
        unsubscribeActivity = onSnapshot(
          query(collection(db, `states/${pbx.context}/queues/${props.queue.exten}/calls`)),
          (docs) => {
            const calls = [];
            docs.forEach((doc) => calls.push(doc.data()));
            setCalls(calls);
          },
          (error) => {
            dispatch(showSnackbar(error.toString()));
          },
        );
      }
    });

    return () => {
      toggleLoadingState(false);
      if (unsubscribeActivity) unsubscribeActivity();
      unsubscribeUser();
    };
  }, [pbx.context, props.queue, dispatch]);

  const memberList = useMemo(() => {
    const memberList = [];

    queueMembersStore.list.map((member) => {
      const queueMember = props.queue.members.find((_queueMember) => _queueMember.exten === member.attributes.exten);

      if (queueMember) {
        memberList.push({
          exten: member.attributes.exten,
          name: member.attributes.name,
          type: member.type,
          paused: queueMember.paused,
          lock: queueMember.lock,
        });
      }
    });

    return memberList;
  }, [queueMembersStore.list, props.queue.members]);

  const callers = calls.length;

  return (
    <FormGroup title="Statistics">
      <Grid size={{ xs: 12, sm: 6 }}>
        <Card variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan="2" sx={{ py: 1, px: 1.5 }}>{`${props.queue.members.length} member${
                    props.queue.members.length === 1 ? "" : "s"
                  }`}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberList.map((member) => (
                  <TableRow key={member.exten}>
                    <TableCell component="th" scope="row" sx={{ p: 0, pl: 1.5, width: 24 }}>
                      <ActivityBadge color={member.paused === 1 ? colors.red : colors.green} size="small">
                        <Avatar
                          text={member.name || ""}
                          icon={member.type === "user" ? undefined : viLocationPinDot}
                          px={24}
                        />
                      </ActivityBadge>
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1.5 }}>{member.name || ""}</TableCell>
                  </TableRow>
                ))}
                {props.queue.members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan="4" sx={{ py: 1, px: 1.5 }}>
                      No members in queue
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Card variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 1, px: 1.5 }}>{`${callers} caller${callers === 1 ? "" : "s"}`}</TableCell>
                  <TableCell align="right" sx={{ py: 1, px: 1.5 }}>
                    Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.length > 0 ? (
                  calls.map((call, key) => {
                    const callTime = new Date(call.Time) / 1000;

                    return (
                      <TableRow key={key}>
                        <TableCell>
                          {call.CallerIDNum}
                          {call.CallerIDName !== "<unknown>" && (
                            <span style={{ opacity: 0.6 }}> &middot; {call.CallerIDName}</span>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Timer startTime={callTime} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan="2" sx={{ py: 1, px: 1.5 }}>
                      No callers in queue
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </FormGroup>
  );
}

QueueCard.propTypes = {
  showQueues: PropTypes.bool,
  queue: PropTypes.object,
};

export default QueueCard;
